import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, ShieldCheck, Loader2, CheckCircle2, MessageSquare } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

export default function SmsOptIn() {
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    const prevTitle = document.title;
    document.title = "SMS Notification Sign-Up & Opt-In Policy — TexasBusinessDispute.com";

    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (created) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    const prevDesc = meta.getAttribute("content");
    meta.setAttribute(
      "content",
      "Sign up for SMS account notifications from TexasBusinessDispute.com (Prefcap Law). Enter your phone, confirm consent, and receive transactional alerts about account activity. Reply STOP to opt out anytime."
    );

    return () => {
      document.title = prevTitle;
      if (created) meta.remove();
      else if (prevDesc !== null) meta.setAttribute("content", prevDesc);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please check the consent box to subscribe.");
      return;
    }
    const digits = phone.replace(/\D/g, "");
    if (digits.length !== 10 && !(digits.length === 11 && digits.startsWith("1"))) {
      toast.error("Please enter a valid 10-digit US phone number.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/sms/subscribe", { phone, consent: true });
      setDone(true);
      toast.success("You're subscribed. A confirmation text will arrive shortly.");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="sms-optin-page">
      <div className="ann-bar">
        Attorney advertisement · Prefcap Law (Houston, Texas) &nbsp;·&nbsp;
        <Link to="/#intake">Begin your assessment &rarr;</Link>
      </div>

      <nav className="nav">
        <div className="container-tx nav-inner">
          <Link to="/" className="brand" style={{ textDecoration: "none" }}>
            <div className="brand-mark">tx</div>
            <div>
              <div className="brand-name">TexasBusinessDispute.com</div>
              <div className="brand-sub">Prefcap Law &middot; Houston, Texas</div>
            </div>
          </Link>
          <div className="nav-links">
            <Link className="nav-link" to="/#how">Process</Link>
            <Link className="nav-link" to="/#practice">Practice</Link>
            <Link className="nav-link" to="/blog">Insights</Link>
          </div>
          <div className="nav-actions">
            <a className="nav-phone" href="tel:+17132912875">
              <Phone size={13} /> (713) 291-2875
            </a>
          </div>
        </div>
      </nav>

      <header className="blog-header">
        <div className="container-tx">
          <div className="eyebrow copper">SMS Notification Sign-Up</div>
          <h1 className="serif blog-header-h">
            Account alerts<br /><em>by text message.</em>
          </h1>
          <p className="blog-header-sub">
            TexasBusinessDispute.com sends short transactional SMS alerts about activity on your account &mdash; intake form submissions and paid consultation bookings. Enter your phone below and confirm consent to subscribe.
          </p>
        </div>
      </header>

      {/* Visible Opt-In Form */}
      <section className="optin-form-section">
        <div className="container-tx" style={{ maxWidth: 640 }}>
          {done ? (
            <div className="optin-success" data-testid="optin-success">
              <CheckCircle2 size={28} />
              <h2 className="serif">You&apos;re subscribed.</h2>
              <p>A confirmation text message will arrive at the number you provided within a few minutes. Reply STOP at any time to unsubscribe.</p>
            </div>
          ) : (
            <form className="optin-form" onSubmit={submit} data-testid="sms-optin-form">
              <div className="optin-form-head">
                <MessageSquare size={16} />
                <h2 className="serif">Subscribe to account alerts</h2>
              </div>

              <label className="optin-label">
                <span>Mobile phone number</span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(713) 555-1234"
                  className="settings-input"
                  autoComplete="tel"
                  inputMode="tel"
                  required
                  data-testid="sms-phone-input"
                />
                <small className="optin-hint">US numbers only. Enter 10 digits.</small>
              </label>

              <label className="optin-consent">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="sms-consent-checkbox"
                />
                <span>
                  By checking this box and clicking <strong>Subscribe</strong>, I expressly consent to receive
                  recurring SMS account-notification messages from <strong>TexasBusinessDispute.com</strong>
                  (a trade name of Prefcap Law) at the mobile number provided above. Messages will alert me
                  about activity on my account, including case-intake form submissions and paid consultation
                  bookings. Message frequency varies. <strong>Message and data rates may apply.</strong>
                  Reply <strong>HELP</strong> for help, or <strong>STOP</strong> at any time to unsubscribe.
                  I confirm I am the subscriber of the phone number provided or have authorization to subscribe it.
                  I have reviewed the <a href="#policy">SMS notification policy</a> below.
                </span>
              </label>

              <button
                type="submit"
                className="optin-submit"
                disabled={busy || !consent}
                data-testid="sms-subscribe-btn"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                {busy ? " Subscribing..." : "Subscribe to SMS alerts"}
              </button>

              <p className="optin-footnote">
                No marketing or promotional messages. No third-party data sharing. Powered by Twilio for US carrier delivery.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Policy */}
      <article className="blog-article" id="policy">
        <div className="container-tx blog-article-inner">
          <div className="blog-prose">

            <h2>SMS notification policy</h2>
            <p>
              <strong>Program name:</strong> TexasBusinessDispute.com Account Notifications<br />
              <strong>Operator:</strong> Prefcap Law (trade name: TexasBusinessDispute.com), principal office Houston, Texas. Lead attorney: Reza Chawla.<br />
              <strong>Type of messages:</strong> Transactional account notifications (no marketing or promotions).<br />
              <strong>Frequency:</strong> Variable, based on account activity. Typically 0&ndash;3 messages per day.<br />
              <strong>Charges:</strong> Message and data rates may apply per your mobile carrier&apos;s plan.
            </p>

            <h2>How to opt in</h2>
            <ol>
              <li>Enter your mobile phone number in the form above.</li>
              <li>Check the consent box.</li>
              <li>Click <strong>Subscribe to SMS alerts</strong>.</li>
              <li>Receive a one-time confirmation SMS at the number you provided.</li>
              <li>Receive subsequent account-notification messages as activity occurs on your account.</li>
            </ol>

            <h2>Example messages</h2>
            <p>You may receive messages similar to the following:</p>
            <blockquote>
              TexasBusinessDispute.com account alert: a new case-intake submission was received
              (ref a1b2c3d4, viability 8/10). Log in at https://texasbusinessdispute.com/admin
              to review. Reply STOP to opt out, HELP for info. Msg &amp; data rates may apply.
            </blockquote>
            <blockquote>
              TexasBusinessDispute.com account alert: a paid consultation booking was completed
              (ref e5f6g7h8, $95). Log in at https://texasbusinessdispute.com/admin to review.
              Reply STOP to opt out, HELP for info. Msg &amp; data rates may apply.
            </blockquote>
            <blockquote>
              TexasBusinessDispute.com: You&apos;re subscribed to account alerts. Reply STOP to opt out,
              HELP for info. Msg &amp; data rates may apply.
            </blockquote>

            <h2>How to opt out</h2>
            <p>
              Reply with any of the following keywords to the SMS short-code or sender number at any time:
            </p>
            <ul>
              <li><strong>STOP</strong></li>
              <li><strong>UNSUBSCRIBE</strong></li>
              <li><strong>END</strong></li>
              <li><strong>QUIT</strong></li>
              <li><strong>CANCEL</strong></li>
            </ul>
            <p>
              You will receive a single acknowledgement message confirming you have been unsubscribed,
              after which no further messages will be sent.
            </p>

            <h2>How to get help</h2>
            <p>
              Reply <strong>HELP</strong> or <strong>INFO</strong> for assistance. You may also contact
              Prefcap Law directly by phone at
              <a href="tel:+17132912875"> (713) 291-2875</a> during normal business hours.
            </p>

            <h2>Privacy &amp; data handling</h2>
            <p>
              The phone number you subscribe is stored only in the firm&apos;s encrypted database for the
              purpose of operating this notification program. Your number is never sold, rented, or
              shared with third-party advertisers. The number is shared only with our SMS delivery
              provider (Twilio) as strictly required to deliver messages to your carrier.
              No information collected here is used for marketing.
            </p>

            <h2>Carrier compliance</h2>
            <p>
              Mobile carriers (including AT&amp;T, T-Mobile, Verizon, and others) do not guarantee
              delivery of any message. Reasonable efforts are made to deliver every message, but
              delivery depends on the recipient&apos;s carrier and network conditions outside the
              firm&apos;s control. By subscribing, you acknowledge that carrier delivery is best-effort.
            </p>

            <h2>Program operator contact</h2>
            <p>
              <strong>Operator:</strong> Prefcap Law (operating as TexasBusinessDispute.com)<br />
              <strong>Responsible attorney:</strong> Reza Chawla, Texas Bar member<br />
              <strong>Principal office:</strong> Houston, Texas<br />
              <strong>Phone:</strong> <a href="tel:+17132912875">(713) 291-2875</a><br />
              <strong>Website:</strong> <a href="https://texasbusinessdispute.com">https://texasbusinessdispute.com</a>
            </p>

            <hr />

            <p style={{ fontSize: 13, color: "var(--muted)" }}>
              Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}.
              This SMS notification program is operated by Prefcap Law for transactional account
              notifications only. By submitting the form above, you affirm you are the subscriber
              of the mobile number provided and consent to receive these notifications under the
              terms described above.
            </p>
          </div>
        </div>
      </article>

      <footer className="footer">
        <div className="container-tx">
          <div className="footer-grid">
            <div>
              <div className="brand" style={{ marginBottom: 14 }}>
                <div className="brand-mark">tx</div>
                <div>
                  <div className="brand-name" style={{ color: "#fbf8f3" }}>TexasBusinessDispute.com</div>
                  <div className="brand-sub">Prefcap Law &middot; Houston, Texas</div>
                </div>
              </div>
              <p>Texas attorney-operated business litigation intake and firm matching service.</p>
              <p style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <ShieldCheck size={14} style={{ color: "var(--copper-soft)" }} /> Confidential &middot; No obligation
              </p>
            </div>
            <div>
              <h4>Quick Links</h4>
              <ul>
                <li><Link style={{ color: "inherit", textDecoration: "none" }} to="/">Home</Link></li>
                <li><Link style={{ color: "inherit", textDecoration: "none" }} to="/blog">Insights</Link></li>
                <li><Link style={{ color: "inherit", textDecoration: "none" }} to="/sms-opt-in">SMS Sign-Up</Link></li>
              </ul>
            </div>
            <div>
              <h4>Contact</h4>
              <ul>
                <li>Prefcap Law</li>
                <li>Houston, Texas</li>
                <li><a style={{ color: "inherit", textDecoration: "none" }} href="tel:+17132912875">(713) 291-2875</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p style={{ marginBottom: 12 }}>
              Referral fee disclosure. TexasBusinessDispute.com is a trade name of Prefcap Law
              (principal office: Houston, Texas), operated by lead attorney Reza Chawla. If a
              referral or any other fee is paid, in accordance with the Texas Disciplinary Rules
              of Professional Conduct (including Rule 1.04(f)), the client will be informed in
              writing of (i) the identities of the lawyers and firms involved, (ii) the division
              of fees between them, and (iii) the responsibility each lawyer is assuming. All
              such terms are documented in the client engagement letter for the matter, and the
              client&apos;s written consent will be obtained before any referral is finalized and
              before any fee is shared.
            </p>
            &copy; {new Date().getFullYear()} TexasBusinessDispute.com &middot; Prefcap Law (Houston, Texas). Attorney advertisement.
          </div>
        </div>
      </footer>
    </div>
  );
}
